import { Component } from '@theme/component';

/**
 * Toasts de MAMU (favoritos, comparar). Se pintan en una región `role="status"` fija en la esquina
 * (arriba a la derecha en escritorio, a todo el ancho en móvil). No depende de otros módulos: cualquier
 * script los pide emitiendo un evento en `document`:
 *
 *   document.dispatchEvent(new CustomEvent('mamu:toast', { detail: {
 *     message: 'Agregado a favoritos',     // obligatorio
 *     icon: 'heart',                       // 'heart' | 'compare' (por defecto 'compare')
 *     actionLabel: 'Ver favoritos',        // opcional, junto con actionHref
 *     actionHref: '/pages/favoritos',
 *     key: 'fav',                          // opcional: un toast nuevo con la misma key sustituye al anterior
 *     duration: 4000,                      // ms; con acción, 6000 por defecto
 *   }}));
 *
 * @typedef {object} ToastDetail
 * @property {string} message
 * @property {string} [icon]
 * @property {string} [actionLabel]
 * @property {string} [actionHref]
 * @property {string} [key]
 * @property {number} [duration]
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list - Región viva donde se insertan los toasts.
 *
 * @extends {Component<Refs>}
 */
class MamuToastRegion extends Component {
  requiredRefs = ['list'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {Map<HTMLElement, number>} */
  #timers = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    document.addEventListener('mamu:toast', this.#onToast, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = null;

    for (const timer of this.#timers.values()) clearTimeout(timer);
    this.#timers.clear();
  }

  /**
   * @param {Event} event
   */
  #onToast = (event) => {
    const detail = /** @type {CustomEvent<ToastDetail>} */ (event).detail;
    if (!detail || typeof detail.message !== 'string' || !detail.message) return;

    this.#show(detail);
  };

  /**
   * @param {ToastDetail} detail
   */
  #show(detail) {
    const { list } = this.refs;
    const maxVisible = 3;

    if (detail.key) {
      for (const existing of list.querySelectorAll('[data-key]')) {
        if (existing instanceof HTMLElement && existing.dataset.key === detail.key) this.#dismiss(existing, true);
      }
    }

    const toast = document.createElement('div');
    toast.className = 'mamu-toast';
    if (detail.key) toast.dataset.key = detail.key;

    const icon = document.createElement('span');
    icon.className = 'mamu-toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.append(this.#cloneIcon(detail.icon ?? 'compare'));

    const text = document.createElement('p');
    text.className = 'mamu-toast__text';
    text.textContent = detail.message;

    toast.append(icon, text);

    if (detail.actionLabel && detail.actionHref && this.#isSafeHref(detail.actionHref)) {
      const action = document.createElement('a');
      action.className = 'mamu-toast__action';
      action.href = detail.actionHref;
      action.append(document.createTextNode(detail.actionLabel));
      action.append(this.#cloneIcon('arrow-right'));
      toast.append(action);
    }

    list.append(toast);

    // Máximo 3 visibles: se retira el más antiguo.
    const toasts = list.querySelectorAll('.mamu-toast');
    for (let i = 0; i < toasts.length - maxVisible; i++) {
      const old = toasts[i];
      if (old instanceof HTMLElement) this.#dismiss(old, true);
    }

    const duration = detail.duration ?? (detail.actionHref ? 6000 : 4000);
    const start = () => this.#timers.set(toast, window.setTimeout(() => this.#dismiss(toast), duration));
    const stop = () => {
      const timer = this.#timers.get(toast);
      if (timer !== undefined) clearTimeout(timer);
      this.#timers.delete(toast);
    };

    // Se pausa mientras el puntero o el foco están dentro (el usuario puede estar leyendo o a punto de usar el enlace).
    toast.addEventListener('pointerenter', stop);
    toast.addEventListener('pointerleave', start);
    toast.addEventListener('focusin', stop);
    toast.addEventListener('focusout', start);
    start();
  }

  /**
   * @param {HTMLElement} toast
   * @param {boolean} [immediate]
   */
  #dismiss(toast, immediate = false) {
    const timer = this.#timers.get(toast);
    if (timer !== undefined) clearTimeout(timer);
    this.#timers.delete(toast);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (immediate || reduceMotion) {
      toast.remove();
      return;
    }

    toast.classList.add('is-leaving');
    window.setTimeout(() => toast.remove(), 200);
  }

  /**
   * Clona un icono de las plantillas `<template data-mamu-toast-icon>` que pinta Liquid (snippet mamu-icon).
   *
   * @param {string} name
   * @returns {Node}
   */
  #cloneIcon(name) {
    const template = this.querySelector(`template[data-mamu-toast-icon="${name}"]`);
    if (template instanceof HTMLTemplateElement) return template.content.cloneNode(true);
    return document.createDocumentFragment();
  }

  /**
   * Solo enlaces del mismo origen o rutas relativas.
   *
   * @param {string} href
   * @returns {boolean}
   */
  #isSafeHref(href) {
    try {
      return new URL(href, window.location.href).origin === window.location.origin;
    } catch {
      return false;
    }
  }
}

if (!customElements.get('mamu-toast-region')) {
  customElements.define('mamu-toast-region', MamuToastRegion);
}
